import type { QueryApiError } from "@features/errors/types.ts";
import type { CodeExpr, ErrorCode } from "@kex-resources/api-errors";

import { createEvent, createStore, sample } from "effector";

type ApiErrorsState = {
  errors: QueryApiError<CodeExpr, ErrorCode>[];
  ignoreByCode: Set<ErrorCode>;
};

export const addApiError = createEvent<QueryApiError<CodeExpr, ErrorCode>>();
export const ignoreApiError = createEvent<ErrorCode>();
export const removeApiError = createEvent<QueryApiError<CodeExpr, ErrorCode>>();
export const stopIgnoreApiError = createEvent<ErrorCode>();

const addApiErrorAccepted = createEvent<QueryApiError<CodeExpr, ErrorCode>>();

export const $apiErrors = createStore<ApiErrorsState>({
  errors: [],
  ignoreByCode: new Set([]),
})
  .on(addApiErrorAccepted, (state, payload) => ({
    ...state,
    errors: [...state.errors, payload],
  }))
  .on(removeApiError, (state, payload) => ({
    ...state,
    errors: state.errors.filter((error) => error !== payload),
  }))
  .on(ignoreApiError, (state, error) => {
    const ignoreByCode = new Set(state.ignoreByCode);
    ignoreByCode.add(error);
    return { ...state, ignoreByCode };
  })
  .on(stopIgnoreApiError, (state, error) => {
    const ignoreByCode = new Set(state.ignoreByCode);
    ignoreByCode.delete(error);
    return { ...state, ignoreByCode };
  });

sample({
  clock: addApiError,
  filter: ({ ignoreByCode }, error) => !ignoreByCode.has(error.code),
  fn: (_, error) => error,
  source: $apiErrors,
  target: addApiErrorAccepted,
});

export const $availableApiErrors = $apiErrors.map(({ errors, ignoreByCode }) =>
  errors.filter((error) => !ignoreByCode.has(error.code)),
);
